import type { FeatureCollection, Geometry } from 'geojson'

export const LEVELS = ['region', 'province', 'district', 'municipality', 'sector'] as const

export type GeographyLevel = (typeof LEVELS)[number]

export const ATLAS_VIEWS = ['map', 'distribution', 'beeswarm', 'rank'] as const
export type AtlasView = (typeof ATLAS_VIEWS)[number]

export type AnalysisScopeKind = 'region' | 'province'

export interface AnalysisScope {
  kind: AnalysisScopeKind
  code: string
}

export type NumberFormat = 'integer' | 'number' | 'percent'

export type ScaleType = 'sequential' | 'diverging' | 'categorical'

export type Additivity = 'additive' | 'ratio' | 'nonadditive'

export type GeographyBasis = 'native' | 'harmonized' | 'mixed'

export type CubeCalculation =
  | {
      type: 'field'
      field: string
    }
  | {
      type: 'ratio'
      numerator?: string
      numeratorFields?: string[]
      denominator?: string
      denominatorFields?: string[]
      scale?: number
    }
  | {
      type: 'ratioToAllOptions'
      field: string
      dimensionId: string
      scale?: number
    }
  | {
      type: 'ratioToGeographyDenominator'
      numerator: string
      denominatorId: string
      scale?: number
    }

export interface MeasureMeta {
  id: string
  label: string
  shortLabel?: string
  field: string
  unit: string
  format: NumberFormat
  decimals?: number
  definition?: string
  paletteId?: string
  scaleType?: Exclude<ScaleType, 'categorical'>
  additivity?: Additivity
  numeratorField?: string
  denominatorField?: string
  statusField?: string
  calculation?: CubeCalculation
  sourceFieldIds?: string[]
  cubeMeasureId?: string
  scale?: number
  center?: number
  availablePeriods?: string[]
  denominatorLineageByPeriod?: Record<string, Record<string, unknown>>
}

export interface MetricLevelMeta {
  periods: string[]
  measures: MeasureMeta[]
  defaultSliceByPeriod: Record<string, string>
  slices: Record<string, SliceMeta>
}

export interface MetricMeta {
  id: string
  label: string
  shortLabel?: string
  description: string
  warning?: string
  definition?: string
  topic?: string
  paletteId?: string
  defaultLevel: GeographyLevel
  defaultMeasure: string
  levels: Partial<Record<GeographyLevel, MetricLevelMeta>>
  sourceIds?: string[]
  filterSetId?: string
  defaultFilters?: Record<string, string>
  requiredFilterIds?: string[]
  lockedFilterIds?: string[]
  fixedOptions?: Record<string, string>
  classificationDimensionId?: string
  classificationDimensionIds?: string[]
  defaultFilterRationale?: string
  calculation?: CubeCalculation
  filterDimensions?: string[]
  /** Build-derived summary used by the sharded indicator index. Source
   * catalogues never need to populate this field. */
  runtimeSummary?: {
    finestLevel: GeographyLevel
    coverageScope: string
    sourceType: 'officialRegister' | 'administrativeHealthcare' | 'surveyEstimate' | 'scheduledService' | 'remoteSensing' | 'modelled' | 'europeanHarmonized'
  }
}

export interface GeographyMeta {
  label: string
  singular: string
  note?: string
}

export interface SourceMeta {
  id: string
  label: string
  /** Concise authoritative identity for map/export credits; never a dataset title. */
  creditLabel?: string
  url: string
  landingPage?: string
  publisher?: string
  description?: string
  period?: string
  releasedAt?: string
  accessedAt?: string
  attribution?: string
  reuseStatus?: 'EXPLICIT_OPEN_LICENCE' | 'PUBLIC_REUSE_TERMS_NOT_EXPLICIT' | 'EXPLICIT_RESTRICTION'
  licence?: string
  licenceUrl?: string
  originalColumns?: string[]
  geographyDomain?: string
  coverageScope?: 'BELGIUM' | 'FLANDERS' | 'WALLONIA' | 'BRUSSELS' | 'PARTICIPATING_MUNICIPALITIES' | 'SOURCE_DEFINED'
  cachedFile?: string
  bytes?: number
  sha256?: string
  translationStatus?: string
}

export interface ClassificationMeta {
  id: string
  metricId: string
  measureId: string
  level: GeographyLevel
  comparisonSetId: string
  breaks: number[]
  method: string
  label?: string
  paletteId?: string
  scaleType?: Exclude<ScaleType, 'categorical'>
  center?: number
}

export interface CoverageMeta {
  featureCount: number
  withData: number
  withDataByField?: Record<string, number>
  suppressedByField?: Record<string, number>
  denominatorCoverage?: number | Record<string, number>
  missing?: number
  ratio?: number
  populationTotal?: number
  populationMapped?: number
  populationCoverage?: number
  coverageScope?: string
  universe?: string
  participatingAreas?: number | string[]
  outOfScope?: number
  inScopeUnavailable?: number
  sampleSize?: number
  confidenceLevel?: number
  statusCounts?: Partial<Record<CanonicalObservationStatus | 'observed' | 'suppressed' | 'unavailable', number>>
  note?: string
}

export type CanonicalObservationStatus =
  | 'regular'
  | 'belowThreshold'
  | 'hidden'
  | 'notApplicable'
  | 'missing'
  | 'empty'

export interface TemporalMeta {
  kind: 'instant' | 'calendarYear' | 'interval' | 'publisherSeries'
    | 'fiscalYear' | 'month' | 'pointInTime' | 'publisherSnapshot'
    | 'referenceYear' | 'serviceDate' | 'sourcePeriod'
    | 'observationDate' | 'referenceDate' | 'releaseDate' | 'coveragePeriod'
  referenceDate?: string
  startDate?: string
  endDate?: string
  sourceKind?: string
  sortKey: string
  inclusivity?: 'closed' | 'left-closed' | 'right-closed' | 'open'
  calendar?: 'gregorian' | string
  note?: string
}

export interface SliceMeta {
  id: string
  periodId: string
  variantId: string
  label: string
  shortLabel: string
  temporal: TemporalMeta
  valuePackId?: string
  cubePackId?: string
  cubeMeasureId?: string
  boundaryPackId: string
  geographyBasis: GeographyBasis
  harmonizedToBoundaryPackId?: string
  crosswalkId?: string
  sourceIds: string[]
  comparisonSetId: string
  coverage: CoverageMeta
  note?: string
}

export interface BoundaryPackLevelMeta {
  assetId: string
  count: number
}

export interface BoundaryPackMeta {
  id: string
  label: string
  vintage: string
  sectorSituationDate?: string
  adminBoundaryVersion?: string
  validFrom: string
  validTo?: string
  sourceIds: string[]
  sourceCrs: string
  outputCrs: string
  hierarchySha256: string
  districtHierarchySha256?: string
  codeListSha256: string
  note?: string
  levels: Partial<Record<GeographyLevel, BoundaryPackLevelMeta>>
}

export interface GeometryAssetMeta {
  id: string
  level: GeographyLevel
  file: string
  format: 'geojson'
  crs: 'EPSG:4326'
  count: number
  bytes: number
  sha256: string
}

export interface FieldMeta {
  label: string
  type: 'integer' | 'number'
  unit: string
  additivity: Additivity
  numeratorField?: string
  denominatorField?: string
  scale?: number
  statusField?: string
}

export interface ValuePackMeta {
  id: string
  familyId: string
  level: GeographyLevel
  periodId: string
  variantId: string
  boundaryPackId: string
  file: string
  format: 'json'
  recordCount: number
  fields: Record<string, FieldMeta>
  bytes: number
  sha256: string
  coverage: CoverageMeta
  qaFile: string
}

export interface CubeGeographyBasisMeta {
  kind: GeographyBasis | 'source-coded'
  comparisonGroup: string
  disclosure?: string
  observationPeriod?: string
  boundaryPackId?: string
}

export interface CubePackMeta {
  id: string
  family: string
  period: string
  boundaryPackId: string
  format: 'json+gzip' | 'json'
  file: string
  rowCount: number
  bytes: number
  sha256: string
  payloadSha256?: string
  qaFile: string
  sourceIds: string[]
  geographyBasis: CubeGeographyBasisMeta
  geographyLevels: GeographyLevel[]
  measureIds: string[]
  dimensionIds: string[]
  dimensionAliases?: Record<string, string>
  fields?: Record<string, FieldMeta>
  statusFields?: Record<string, CubeStatusFieldMeta>
  valueSemantics?: Record<string, unknown>
  geographyDenominators?: Record<string, {
    unit: string
    periodId: string
    levels: GeographyLevel[]
    sourceIds: string[]
  }>
}

export interface CubeGeographyDenominator {
  id: string
  label: string
  unit: string
  periodId: string
  levels: Partial<Record<GeographyLevel, Array<number | null>>>
  lineageByLevel: Partial<Record<GeographyLevel, Record<string, unknown>>>
}

export interface CubePack {
  schemaVersion: string
  family: string
  period: string
  boundaryPackId: string
  geography: {
    levels: GeographyLevel[]
    codeDictionaries: string[][]
    unmappedIndex: number
  }
  dimensionIds: string[]
  optionDictionaries: string[][]
  measureIds: string[]
  fields?: Record<string, FieldMeta>
  statusFields?: Record<string, CubeStatusFieldMeta>
  valueSemantics: Record<string, unknown>
  geographyDenominators?: Record<string, CubeGeographyDenominator>
  rows: Array<Array<number | null>>
}

export interface CubeStatusFieldMeta {
  kind: 'dimension'
  dimensionId: string
  observedCode?: string
  suppressedCodes?: string[]
  unavailableCodes?: string[]
  canonicalCodeMap?: Record<string, CanonicalObservationStatus>
  canonicalStatusField?: string
}

export interface DimensionOptionMeta {
  code: string
  label: string
  description?: string
  translationStatus?: string
}

export interface DimensionMeta {
  id: string
  label: string
  description?: string
  options: DimensionOptionMeta[]
  defaultCodes?: string[]
  required?: boolean
  selectionMode?: 'single' | 'multiple'
  missingCode?: string
  suppressedCode?: string
  derivation?: string
}

export interface FilterSetMeta {
  id: string
  label: string
  dimensionIds: string[]
  defaultFilters?: Record<string, string[]>
  requiredSingleSelectDimensions?: string[]
}

export interface ValuePack {
  schemaVersion: string
  id: string
  familyId: string
  level: GeographyLevel
  periodId: string
  variantId: string
  boundaryPackId: string
  temporal: TemporalMeta
  geographyBasis: GeographyBasis
  sourceIds: string[]
  fields: Record<string, FieldMeta>
  records: Record<string, ValueRecord>
  coverage: CoverageMeta
  qa: {
    status: 'passed' | 'failed'
    qaFile: string
    checkIds: string[]
  }
}

export interface PaletteMeta {
  id: string
  label: string
  type: ScaleType
  colors: string[]
  missingColor: string
  suppressedColor?: string
  semanticRole?: string
  direction?: string
  center?: number
  zeroPolicy?: string
  missingPolicy?: string
  allowedClassCounts?: number[]
  provenance?: string
}

export interface ComparisonSetMeta {
  id: string
  label: string
  metricId: string
  level: GeographyLevel
  sliceIds: string[]
  boundaryPolicy: 'same-pack' | 'native-snapshots' | 'harmonized'
  changeAllowed: boolean
  coverageScope?: string
  rankingScope?: string
  note?: string
}

export interface UnavailableDatasetMeta {
  id: string
  label: string
  reasonCode: string
  reason: string
  evidence?: Record<string, unknown>
}

export interface ChartRecommendationMeta {
  type: string
  label: string
  sourceFamily?: string
  dimensionId?: string
  measureId?: string
  recommendedPolicy?: string
  [key: string]: unknown
}

export type ValueRecord = Record<string, number | string | null>

export interface Catalog {
  schemaVersion: '2.0.0'
  generatedAt: string
  dataVersion: string
  sources: SourceMeta[]
  geographies: Record<GeographyLevel, GeographyMeta>
  boundaryPacks: Record<string, BoundaryPackMeta>
  geometryAssets: Record<string, GeometryAssetMeta>
  valuePacks: Record<string, ValuePackMeta>
  cubePacks?: Record<string, CubePackMeta>
  dimensions?: Record<string, DimensionMeta>
  filterSets?: Record<string, FilterSetMeta>
  metrics: MetricMeta[]
  palettes: Record<string, PaletteMeta>
  comparisonSets: Record<string, ComparisonSetMeta>
  classifications: Record<string, ClassificationMeta>
  localeAssets?: Partial<Record<'en' | 'fr' | 'nl' | 'de', {
    file: string
    bytes: number
    sha256: string
    keyCount: number
  }>>
  unavailableDatasets?: UnavailableDatasetMeta[]
  charts?: Record<string, ChartRecommendationMeta>
  compatibility: {
    v1UrlParams: string[]
    periodsAreMetricLocal: boolean
    variantParam: string
    defaultVariant: string
  }
  qa: {
    status: 'passed' | 'failed'
    checksPassed: number
    checksTotal: number
    qaFile: string
    [key: string]: unknown
  }
}

export interface GeographyProperties {
  code: string
  name: string
  nameNl?: string
  nameFr?: string
  nameDe?: string
  parentCode?: string | null
  districtCode?: string
  provinceCode?: string
  regionCode?: string
  areaKm2?: number
  provinceEquivalent?: boolean
  _value?: number | null
  _color?: string
}

export type GeographyFeatureCollection = FeatureCollection<
  Geometry,
  GeographyProperties
>

export interface Selection {
  metricId: string
  level: GeographyLevel
  measureId: string
  period: string
  variant?: string
  /** Reproducible user override; omitted means the catalogue scientific default. */
  palette?: string
  /** Ordered user-defined colour stops, present only when `palette` is `custom`. */
  customPalette?: string[]
  /** Omitted keeps catalogue breaks; overrides deliberately use current-slice quantiles. */
  classMode?: 'adaptive' | 'custom'
  /** Requested current-slice quantile count for `classMode: custom`. */
  classCount?: number
  /** Omitted/true keeps area boundaries visible; false hides the shared line layer. */
  showBoundaries?: boolean
  filters?: Record<string, string>
  /** Shareable analytical canvas; omitted is the default map view. */
  view?: AtlasView
  /** Shared analytical area; omitted means the complete Belgian comparison set. */
  scope?: AnalysisScope
  /** User map-grammar choice; `auto` follows the measure's statistical semantics. */
  mapEncoding?: 'auto' | 'choropleth' | 'proportional-symbol' | 'cartogram'
}

export interface TooltipState {
  x: number
  y: number
  flipX?: boolean
  flipY?: boolean
  properties: GeographyProperties
}
